import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ToastService } from '@techspert-io/user-alerts';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { SEARCH_API_BASE_URL } from '../../search/services/search.service';
import {
  IOmnisearchProfile,
  IOmnisearchProfileResponse,
  OmnisearchEntity,
  OmnisearchQueryResponse,
} from '../models/omnisearch.models';

@Injectable({
  providedIn: 'root',
})
export class OmnisearchService {
  // Connect infrastructure
  private readonly omnisearch = '/omnisearch';

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    @Inject(SEARCH_API_BASE_URL) private searchApiBaseUrl: string
  ) {}

  search(query: string): Observable<OmnisearchQueryResponse[]> {
    if (query.length <= 2) {
      return of([]);
    }

    return forkJoin([this.query(query), this.searchProfiles(query)]).pipe(
      map(([redisResults, profileResults]) => [
        ...profileResults,
        ...redisResults,
      ])
    );
  }

  private query(query: string): Observable<OmnisearchQueryResponse[]> {
    const params = new HttpParams().set('query', query);
    return this.http
      .get<OmnisearchQueryResponse[]>(`${this.omnisearch}/search`, { params })
      .pipe(
        catchError((error) => {
          this.toastService.sendMessage(
            'Failed to fetch omnisearch results',
            'error'
          );
          return of([]);
        })
      );
  }

  private searchProfiles(term: string): Observable<IOmnisearchProfile[]> {
    const limit: number = 50;

    return this.http
      .post<IOmnisearchProfileResponse>(
        `${this.searchApiBaseUrl}/expert-profile-query/omnisearch`,
        { term, limit }
      )
      .pipe(
        map((response) =>
          response.profiles.map<IOmnisearchProfile>(({ profileId, ...p }) => ({
            ...p,
            entity: OmnisearchEntity.profiles,
            id: profileId,
          }))
        ),
        catchError((error) => {
          console.error('Profile search error:', error);
          this.toastService.sendMessage(
            'Failed to fetch profile search results',
            'error'
          );
          return of([]);
        })
      );
  }
}
